import dayjs, { Dayjs } from 'dayjs';

import {
  REPORTING_TRIAL_ENABLED,
  useReportingTrialStatusQuery,
  useStartReportingTrialMutation,
  useMarkOrgEligibleForReportingMonetizationMutation,
} from '@cast/shared-trial-data-access';
import {
  ReportingTrialStatusEnum,
  ReportingTrialStatusResponse,
  type ReportingMonetizationTier,
} from '@cast/types';

import {
  deriveConsoleAccess,
  doesReportingMonetizationApply,
} from './console-access/deriveConsoleAccess';
import {
  useReportingTrialEligibleDevOverride,
  useReportingTrialStateDevOverride,
  useReportingTrialTierDevOverride,
} from './reportingTrialDevOverrides';
import {
  reportingTierToPlanId,
  type ReportingPaidPlanId,
} from './reportingPlans';
import type { ReportingTrialState } from './reportingTrialState';
import { useCanFetchReportingMonetization } from './useCanFetchReportingMonetization';
import { useTrial } from './TrialProvider';

export type { ReportingTrialState } from './reportingTrialState';

export type ReportingTrial = {
  isLoading: boolean;
  hasError: boolean;
  /** Indicates if organization is eligible for the reporting trial (does not mean that trial has started) */
  isEligible: boolean;
  /** Trial is currently active */
  isActive: boolean;
  /** Indicates eligible organization that has not started or has finished the trial. */
  isPreTrial: boolean;
  /** Trial has expired and is in the grace period window */
  isInGracePeriod: boolean;
  /** Trial is about to expire (within 7 days) */
  isAboutToExpire: boolean;
  state: ReportingTrialState;
  expiresInDays: number;
  expiresInDaysGracePeriod?: number;
  /** Calendar end of the active trial window (for copy like "before 9 October"). */
  expiresAt?: Dayjs;
  /** Stripe subscription tier when paid (raw BE enum). */
  tier?: ReportingMonetizationTier;
  /** FE plan id mapped from Stripe tier (Scale ↔ Premium). */
  currentPaidPlanId?: ReportingPaidPlanId;
  /** End of current Stripe billing period (next payment). */
  stripeCurrentPeriodEnd?: Dayjs;
  startTrial: () => void;
  isStartingTrial: boolean;
  markEligible: (options?: {
    onSuccess?: () => void;
    onError?: (error: Error) => void;
  }) => void;
  isMarkingEligible: boolean;
};

/** Fallback day counts when Dev Toolbox overrides state without API dates. */
const MOCK_ACTIVE_DAYS_LEFT = 30;
const MOCK_GRACE_DAYS_LEFT = 5;

/**
 * Returns the number of calendar days remaining until the given date.
 * Returns 0 when the end date falls on today's calendar day.
 */
const getExpiresInDays = (endDate: Dayjs | null | undefined): number => {
  if (!endDate?.isValid()) {
    return Infinity;
  }

  const now = dayjs();

  return endDate.utc().startOf('day').diff(now.utc().startOf('day'), 'day');
};

const getReportingTrialState = (
  data?: ReportingTrialStatusResponse
): ReportingTrialState => {
  if (!data) return 'pre-trial';

  if (data.status === ReportingTrialStatusEnum.PAID) return 'paid';

  const now = dayjs();
  const gracePeriodEnd = data.trialGracePeriodEndsAt
    ? dayjs(data.trialGracePeriodEndsAt)
    : null;

  if (
    data.status === ReportingTrialStatusEnum.TRIAL_EXPIRED &&
    gracePeriodEnd?.isValid() &&
    now.isBefore(gracePeriodEnd)
  ) {
    return 'grace-period';
  }

  if (data.status === ReportingTrialStatusEnum.TRIAL_ACTIVE) {
    return 'active';
  }

  if (data.status === ReportingTrialStatusEnum.TRIAL_EXPIRED) {
    return 'post-trial';
  }

  return 'pre-trial';
};

/**
 * How reporting-monetization trial works from BE side:
 *
 * 1) Trial starts when status transitions from TRIAL_PENDING to TRIAL_ACTIVE
 * 2) Trial lasts for a set period (trialStartedAt → trialExpiresAt)
 * 3) After expiry, a grace period begins (trialExpiresAt → trialGracePeriodEndsAt, typically 7 days)
 * 4) After the grace period ends, the trial is fully expired
 *
 * Paid conversion:
 * - FE opens Stripe Checkout via POST …/checkout-session (with baseUrl)
 * - On return, FE confirms via POST …/checkout-session/confirm (?session_id=)
 * - Paid plan changes use POST …/change-plan → Stripe portal confirmation URL
 * - Manage billing uses POST …/billing-portal (with baseUrl)
 *
 * Statuses:
 * - UNSPECIFIED: default, org has not been marked eligible
 * - TRIAL_PENDING: eligible but trial not yet started
 * - TRIAL_ACTIVE: trial is running
 * - TRIAL_EXPIRED: trial period has ended
 * - PAID: set by BE after successful Stripe payment
 * - PAYMENT_EXPIRED: organization stopped paying (not used for now)
 *
 * Console limiting (sidebar / automation) is NOT decided here — see
 * `console-access/deriveConsoleAccess` (reporting + PLG together).
 */
export const useReportingTrial = (organizationId?: string): ReportingTrial => {
  const { canFetch, isAbilityLoading } = useCanFetchReportingMonetization();
  const {
    data,
    isLoading: isStatusLoading,
    error,
  } = useReportingTrialStatusQuery({
    enabled: REPORTING_TRIAL_ENABLED && canFetch,
    organizationId,
  });

  const startTrialMutation = useStartReportingTrialMutation();
  const markEligibleMutation =
    useMarkOrgEligibleForReportingMonetizationMutation();

  const stateDevOverride = useReportingTrialStateDevOverride();
  const eligibleDevOverride = useReportingTrialEligibleDevOverride();
  const tierDevOverride = useReportingTrialTierDevOverride();

  const computedState = getReportingTrialState(data);
  const state: ReportingTrialState = stateDevOverride ?? computedState;

  const isEligible =
    eligibleDevOverride ??
    (stateDevOverride != null ? true : data?.eligibleForTrial ?? false);

  // Fail closed while loading; ignore loading once errored so refetches cannot
  // flap limitAutomationConsole (CSU-6357).
  const isLoading =
    stateDevOverride != null
      ? false
      : (REPORTING_TRIAL_ENABLED && isAbilityLoading) ||
        (canFetch && isStatusLoading && !error);

  const trialEnd: Dayjs | null = data?.trialExpiresAt
    ? dayjs(data.trialExpiresAt)
    : null;
  const gracePeriodEnd: Dayjs | null = data?.trialGracePeriodEndsAt
    ? dayjs(data.trialGracePeriodEndsAt)
    : null;

  const apiExpiresInDays = getExpiresInDays(trialEnd);
  const apiExpiresInDaysGracePeriod = getExpiresInDays(gracePeriodEnd);

  // When Dev Toolbox overrides state and API has no dates, use mock day counts
  // so badge/UI copy is usable locally.
  const expiresInDays =
    stateDevOverride === 'active' && !isFinite(apiExpiresInDays)
      ? MOCK_ACTIVE_DAYS_LEFT
      : apiExpiresInDays;
  const expiresInDaysGracePeriod =
    stateDevOverride === 'grace-period' &&
    !isFinite(apiExpiresInDaysGracePeriod)
      ? MOCK_GRACE_DAYS_LEFT
      : apiExpiresInDaysGracePeriod;

  const isActive = state === 'active';
  const isInGracePeriod = state === 'grace-period';

  const isAboutToExpire =
    isActive &&
    isFinite(expiresInDays) &&
    expiresInDays >= 0 &&
    expiresInDays <= 7;

  const isPreTrial = isEligible && state === 'pre-trial';

  const expiresAt = trialEnd?.isValid()
    ? trialEnd
    : stateDevOverride === 'active' && Number.isFinite(expiresInDays)
    ? dayjs().add(expiresInDays, 'day')
    : undefined;

  const tier = tierDevOverride ?? data?.tier;
  const currentPaidPlanId =
    state === 'paid' ? reportingTierToPlanId(tier) : undefined;

  const stripePeriodEnd = data?.stripeCurrentPeriodEnd
    ? dayjs(data.stripeCurrentPeriodEnd)
    : undefined;
  const stripeCurrentPeriodEnd = stripePeriodEnd?.isValid()
    ? stripePeriodEnd
    : stateDevOverride === 'paid'
    ? dayjs().add(30, 'day')
    : undefined;

  return {
    isLoading,
    hasError: stateDevOverride != null ? false : !!error,
    isEligible,
    isActive,
    isPreTrial,
    isInGracePeriod,
    isAboutToExpire,
    state,
    expiresInDays,
    expiresInDaysGracePeriod,
    expiresAt,
    tier,
    currentPaidPlanId,
    stripeCurrentPeriodEnd,
    startTrial: () => startTrialMutation.mutate(),
    isStartingTrial: startTrialMutation.isPending,
    markEligible: (options) => markEligibleMutation.mutate(undefined, options),
    isMarkingEligible: markEligibleMutation.isPending,
  };
};

/**
 * @deprecated Prefer `deriveConsoleAccess` / `useConsoleAccess().limitAutomationConsole`.
 * Reporting-only approximation (ignores PLG): eligible and not paid.
 */
export const shouldLimitConsoleForReporting = (
  trial: Pick<ReportingTrial, 'isEligible' | 'state'>
): boolean => trial.isEligible && trial.state !== 'paid';

/** Hide Automation nav / reporting-limited surfaces (reporting + PLG). Fail closed while status loads. */
export const useShouldLimitConsoleForReporting = (
  organizationId?: string
): boolean => {
  const reporting = useReportingTrial(organizationId);
  const plg = useTrial();
  if (reporting.isLoading) {
    return true;
  }
  return deriveConsoleAccess(
    { isEligible: reporting.isEligible, state: reporting.state },
    { state: plg.state }
  ).limitAutomationConsole;
};

export { doesReportingMonetizationApply };

export const useDoesReportingMonetizationApply = (): boolean =>
  doesReportingMonetizationApply(useReportingTrial());
